import { readEvent, dispatchEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import swipeEvents from '@kissui/helpers/src/swipeEvents'
import createProps from '@kissui/helpers/src/props.helpers'
import {
    recursiveSwitchAriaHidden,
    setFirstAndLastFocusable,
    focusin
} from '@kissui/helpers/src/assets/js/scopedKeyboardNav'
import {
    EVENT_QS_OPEN,
    EVENT_QS_CLOSE,
    EVENT_QS_CLOSED,
    EVENT_QS_KEY_DOWN,
    EVENT_SWIPED_DOWN,
    EVENT_ADD_TO_CART,
    TIME_FAST
} from '@kissui/components'
import Overlay from './components/overlay'
import viewportHelper from '@kissui/helpers/src/viewport.helpers'
import { QUANTITY_SELECTOR_ID } from './constants'

const STATE_OPEN = 'isOpen'
const SCROLL_LOCK = 'scroll-lock'

class Qs extends HTMLElement {
    constructor() {
        super()
        this.props = {}

        this.boundOpen = this.open.bind(this)
        this.boundClose = this.close.bind(this)
        this.boundEscapeClose = this.escapeClose.bind(this)
        this.boundFocus = this.focus.bind(this)
        this.boundFocusin = this.focusIn.bind(this)
        this.boundSwipeDown = this.swipeDown.bind(this)
        this.boundNumericKeypress = this.numericKeypress.bind(this)
        this.boundValueChanged = this.valueChanged.bind(this)
        this.boundAddToCart = this.addToCart.bind(this)
    }

    connectedCallback() {
        this.props = createProps(this.attributes)
        const { id } = this.props
        this.setAttribute('id', id || QUANTITY_SELECTOR_ID)

        this.style.display = 'none'
        this.ariaModal = false
        this.oldValue = ''

        this.render()
    }

    disconnectedCallback() {
        Overlay.close()
        if (!this.hasEvent) {
            return
        }
        this.unbindEvent()
    }

    render() {
        if (this.hasEvent) {
            this.unbindEvent()
        }

        const {
            a11y_qs_label,
            a11y_qs_description,
            a11y_predefined_quantity,
            a11y_predefined_description,
            a11y_custom_quantity,
            a11y_custom_description,
            label_choose_quantity,
            label_button_ok,
            step,
            max
        } = this.props

        // FYI : The role is dialog, not alertdialog (only used for alerts / warnings ...)
        this.setAttribute('role', 'dialog')

        // Set the popin aria-hidden by default
        this.setAttribute('aria-hidden', 'true')

        this.innerHTML = `
            <form aria-label="${a11y_qs_label}" aria-description="${a11y_qs_description}">
                <fieldset aria-label="${a11y_predefined_quantity}" aria-description="${a11y_predefined_description}">
                    ${this.renderItems()}
                </fieldset>
                <fieldset class="custom" aria-label="${a11y_custom_quantity}" aria-description="${a11y_custom_description}">
                    <div>
                        <input id="other_quantity" type="number" min="0" step="${step}" max="${max}" aria-label="${label_choose_quantity}" pattern="[0-9]*"/>
                        <label for="other_quantity" aria-hidden="true" class="t-2xs-500-sl">${label_choose_quantity}</label>
                    </div>
                    <button type="button" class="t-sm-400-caps">${label_button_ok}</button>
                </fieldset>
            </form>
        `

        // Expected css to be downloaded
        setTimeout(() => {
            this.style.display = 'inherit'
        }, 100)

        // Set the first and last focusable elements
        setFirstAndLastFocusable(this)

        this.firstButtonElement = this.querySelector('ul li:first-child button')
        this.buttonElements = this.querySelectorAll('ul button')
        this.inputElement = this.querySelector('input')
        this.buttonOK = this.querySelector('.custom button')
        this.bindEvent()
    }

    renderItems() {
        const { quantities = [] } = this.props

        const renderedItems = quantities.reduce(
            (output, item) => `${output}${this.renderItem(item)}`,
            ''
        )

        return `<ul>${renderedItems}</ul>`
    }

    renderItem(item) {
        const { a11y_add_label } = this.props
        return `<li><button aria-label="${a11y_add_label} ${item}" value="${item}" type="button" class="t-2xs-500-sl">${item}</button></li>`
    }

    addLock() {
        if (viewportHelper.is.mobile) {
            document.documentElement.classList.add(SCROLL_LOCK)
        }
        Overlay.open(this.id, true)
    }

    removeLock() {
        if (viewportHelper.is.mobile) {
            document.documentElement.classList.remove(SCROLL_LOCK)
        }
        Overlay.close()
    }

    switchAriaModal() {
        this.ariaModal = !this.ariaModal
        // This is the future way to scope the SR focus to a modal, but it's not supported yet by all SR
        this.setAttribute('aria-modal', this.ariaModal)
        // So we need to set aria-hidden=false to all brothers and brothers of parent until body
        recursiveSwitchAriaHidden(this, this.ariaModal)
    }

    setOpeningPosition() {
        const parentRect = this.parentNode.getBoundingClientRect()
        const estimatedWidth = 380 // Tested to be ISO to IT addToCart (was 280)

        if (parentRect.top < estimatedWidth) {
            this.classList.add('open-bottom')
        } else {
            this.classList.remove('open-bottom')
        }

        if (document.body.scrollWidth - parentRect.right < estimatedWidth) {
            this.classList.add('open-left')
        } else {
            this.classList.remove('open-left')
        }

        if (parentRect.left < estimatedWidth) {
            this.classList.add('open-right')
        } else {
            this.classList.remove('open-right')
        }
    }

    open(e) {
        if (this.isOpen) {
            return
        }

        // Get sku and price from event args
        const event = readEvent(e)
        if (event.id !== this.id) {
            return
        }

        this.isOpen = true
        // Transitionend listener is fired multiple times (fore each css property)
        // so we need to instantiate it only when we open or close, and then we remove it on focusin
        this.addEventListener('transitionend', this.boundFocus)
        document.addEventListener('focusin', this.boundFocusin)
        this.openingSource = document.activeElement

        if (!viewportHelper.is.mobile) {
            this.setOpeningPosition()
        }
        this.addLock()
        this.setAttribute('aria-hidden', 'false')

        return new Promise(resolve => {
            setTimeout(() => {
                this.classList.add(STATE_OPEN)
                resolve()
            }, TIME_FAST)
        })
    }

    close() {
        if (!this.isOpen) {
            return
        }

        this.isOpen = false
        // Transitionend listener is fired multiple times (fore each css property)
        // so we need to instantiate it only when we open or close and then we remove it on focusin
        this.addEventListener('transitionend', this.boundFocus)
        document.removeEventListener('focusin', this.boundFocusin)

        this.removeLock()
        this.setAttribute('aria-hidden', 'true')
        this.classList.remove(STATE_OPEN)

        dispatchEvent({
            eventName: EVENT_QS_CLOSED,
            args: { id: this.id },
            element: this
        })
    }

    focus() {
        this.removeEventListener('transitionend', this.boundFocus)

        if (this.isOpen) {
            this.firstButtonElement.focus()
        } else {
            this.openingSource.focus()
        }
    }

    focusIn(e) {
        focusin(e, this, true)
    }

    escapeClose(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.close()
        }
    }

    swipeDown() {
        if (this.isOpen) {
            this.close()
        }
    }

    numericKeypress(e) {
        // Force to enter only numeric values + allow only a maximum of characters to be ISO as max number of chars
        if (!/\d/i.test(e.key) || e.target.value.length + 1 > this.props.max.length) {
            e.preventDefault()
        }
    }

    valueChanged(e) {
        // This is to hide label used as placeholder
        if (e.target.value !== '') {
            e.target.classList.add('filled')
        } else {
            e.target.classList.remove('filled')
        }

        // If the field value is above max quantity
        if (e.target.value > this.props.max) {
            e.target.value = this.props.max
        }
    }

    async addToCart(e) {
        let qty = e.target.value
        if (e.target === this.buttonOK) {
            qty = this.inputElement.value
            if (qty === '') {
                return
            }
        }

        dispatchEvent({
            eventName: EVENT_ADD_TO_CART,
            args: { quantity: qty },
            element: this
        })

        this.close()
    }

    bindEvent() {
        this.hasEvent = true
        window.addEventListener(EVENT_QS_OPEN, this.boundOpen)
        window.addEventListener(EVENT_QS_CLOSE, this.boundClose)
        document.addEventListener(EVENT_QS_KEY_DOWN, this.boundEscapeClose)

        // Buttons AddToCart
        this.buttonElements.forEach(element => {
            element.boundAddToCart = this.addToCart.bind(element)
            element.addEventListener('click', this.boundAddToCart)
        })

        // Input number
        if (this.inputElement) {
            this.inputElement.addEventListener('keypress', this.boundNumericKeypress)
            this.inputElement.addEventListener('change', this.boundValueChanged)
        }

        // Button OK
        if (this.buttonOK) {
            this.buttonOK.addEventListener('click', this.boundAddToCart)
        }

        // Swipe
        if (viewportHelper.is.mobile) {
            swipeEvents(window, document, this)
            document.addEventListener(EVENT_SWIPED_DOWN, this.boundSwipeDown)
            this.hasSwipeEvent = true
        }
    }

    unbindEvent() {
        window.removeEventListener(EVENT_QS_OPEN, this.boundOpen)
        window.removeEventListener(EVENT_QS_CLOSE, this.boundClose)
        document.removeEventListener(EVENT_QS_KEY_DOWN, this.boundEscapeClose)

        // Buttons AddToCart
        this.buttonElements.forEach(element => {
            element.removeEventListener('click', this.boundAddToCart)
        })

        // Input number
        if (this.inputElement) {
            this.inputElement.removeEventListener('keypress', this.boundNumericKeypress)
            this.inputElement.removeEventListener('change', this.boundValueChanged)
        }

        // Button OK
        if (this.buttonOK) {
            this.buttonOK.removeEventListener('click', this.boundAddToCart)
        }

        // Swipe
        if (this.hasSwipeEvent) {
            document.removeEventListener(EVENT_SWIPED_DOWN, this.boundSwipeDown)
            this.hasSwipeEvent = false
        }
        this.hasEvent = false
    }
}

customElements.get('nb-qs') || customElements.define('nb-qs', Qs)
export default Qs
